import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import type { TourMedia } from './tour-video-slot';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Each lesson mounts a fresh instance so its clip starts at the beginning. */
export function InspirationGuideVideo({ media }: { media: TourMedia }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeRef = useRef(false);
  const userPausedRef = useRef(false);
  const explicitPlayRef = useRef(false);
  const [retry, setRetry] = useState(0);
  const [failed, setFailed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches === true,
  );
  const [showPoster, setShowPoster] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches === true,
  );

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setBlocked(false);
    void video.play().then(() => {
      if (!activeRef.current) video.pause();
    }).catch(() => {
      if (activeRef.current) {
        setPlaying(false);
        setBlocked(true);
        if (window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches) setShowPoster(true);
      }
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    activeRef.current = true;
    const preference = window.matchMedia?.(REDUCED_MOTION_QUERY);
    video.src = media.src;
    if (!preference?.matches || explicitPlayRef.current) video.load();
    const syncPreference = () => {
      const reduced = preference?.matches === true;
      setReducedMotion(reduced);
      if (reduced && !explicitPlayRef.current) {
        setShowPoster(true);
        video.pause();
        setPlaying(false);
      } else {
        setShowPoster(false);
        if (!userPausedRef.current) play();
      }
    };
    syncPreference();
    const onPreferenceChange = () => {
      if (preference?.matches) explicitPlayRef.current = false;
      syncPreference();
    };
    preference?.addEventListener('change', onPreferenceChange);
    return () => {
      activeRef.current = false;
      preference?.removeEventListener('change', onPreferenceChange);
      video.pause();
      video.removeAttribute('src');
      video.load(); // Abort the previous lesson's request as soon as it unmounts.
    };
  }, [media.src, retry, play]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      userPausedRef.current = true;
      video.pause();
      setPlaying(false);
      return;
    }
    userPausedRef.current = false;
    explicitPlayRef.current = true;
    setShowPoster(false);
    play();
  };

  const retryPlayback = () => {
    userPausedRef.current = false;
    explicitPlayRef.current = true;
    setFailed(false);
    setBlocked(false);
    setShowPoster(false);
    setRetry(value => value + 1);
  };

  const playbackState = failed ? 'failed' : blocked ? 'blocked' : showPoster ? 'reduced-motion' : playing ? 'playing' : 'paused';
  return <div className="inspiration-guide-video" data-testid="inspiration-guide-video" data-playback-state={playbackState}>
    <div className="inspiration-guide-video-frame">
      <video
        ref={videoRef}
        data-testid="inspiration-guide-clip"
        aria-label={media.label}
        src={media.src}
        poster={media.poster}
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        tabIndex={-1}
        preload={reducedMotion ? 'none' : 'metadata'}
        onPlaying={() => { setPlaying(true); setBlocked(false); setFailed(false); }}
        onPause={() => setPlaying(false)}
        onError={() => { if (activeRef.current) { setFailed(true); setPlaying(false); } }}
      />
      {(failed || showPoster) && media.poster && <img className="inspiration-guide-video-poster" src={media.poster} alt={media.label} />}
      {failed && <span className="inspiration-guide-video-status" role="status">Video unavailable. The lesson text is still available.</span>}
      {!failed && blocked && <span className="inspiration-guide-video-status" role="status">Playback was blocked. Select Play video to try again.</span>}
      {!failed && !blocked && showPoster && <span className="inspiration-guide-video-status" role="status">Animation paused for reduced motion.</span>}
    </div>
    <div className="inspiration-guide-video-controls">
      {failed
        ? <button type="button" onClick={retryPlayback}><RotateCcw size={14} aria-hidden="true" />Retry video</button>
        : <button type="button" onClick={togglePlayback}>{playing ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}{playing ? 'Pause video' : 'Play video'}</button>}
      <div className="inspiration-guide-video-meta"><span>Muted · loops until Next</span><a href={media.src} target="_blank" rel="noopener noreferrer" aria-label="Open this lesson video full size">View larger</a></div>
    </div>
  </div>;
}
